import axios, { AxiosInstance } from 'axios';
import { NamespaceInfo, EverestConfig } from '../types';

export class SystemService {
  private client: AxiosInstance;

  constructor(baseUrl: string = 'http://localhost:8080/api') {
    this.client = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Get system configuration
  async getSystemConfig(): Promise<EverestConfig> {
    try {
      const response = await this.client.get(`/v1/config`);
      return response.data;
    } catch (_error) {
      console.warn(
        'Falling back to mock data: API call to get system config failed',
        _error,
      );
      return this.getMockConfig();
    }
  }

  // Get available namespaces
  async getNamespaces(): Promise<NamespaceInfo[]> {
    try {
      const response = await this.client.get(`/v1/namespaces`);
      return response.data;
    } catch (_error) {
      console.warn(
        'Falling back to mock data: API call to get namespaces failed',
        _error,
      );
      return this.getMockNamespaces();
    }
  }

  // Add namespace
  async addNamespace(name: string): Promise<NamespaceInfo> {
    try {
      const response = await this.client.post(`/v1/namespaces`, { name });
      return response.data;
    } catch (_error) {
      console.warn(
        `Falling back to mock data: API call to add namespace '${name}' failed`,
        _error,
      );
      return {
        name,
        status: 'active',
        operators: {
          mongodb: true,
          postgresql: true,
          mysql: true,
        },
      };
    }
  }

  // Remove namespace
  async removeNamespace(name: string): Promise<void> {
    try {
      await this.client.delete(`/v1/namespaces/${name}`);
    } catch (_error) {
      console.log(`Mock: Removed namespace ${name}`);
    }
  }

  // Get Kubernetes cluster info
  async getClusterInfo(): Promise<any> {
    try {
      const response = await this.client.get(`/v1/kubernetes`);
      return response.data;
    } catch (_error) {
      console.warn(
        'Falling back to mock data: API call to get Kubernetes cluster info failed',
        _error,
      );
      return {
        version: '1.28.5',
        nodes: 3,
        totalCPU: '24',
        totalMemory: '96Gi',
        totalStorage: '1Ti',
      };
    }
  }

  // Mock data helpers
  private getMockConfig(): EverestConfig {
    return {
      version: '1.2.0',
      k8sVersion: '1.28.5',
      namespaces: ['everest', 'production', 'staging'],
      operators: {
        postgresql: {
          version: '2.3.1',
          status: 'installed',
        },
        mysql: {
          version: '1.14.0',
          status: 'installed',
        },
        mongodb: {
          version: '1.15.0',
          status: 'installed',
        },
      },
    };
  }

  private getMockNamespaces(): NamespaceInfo[] {
    return [
      {
        name: 'everest',
        status: 'active',
        operators: {
          mongodb: true,
          postgresql: true,
          mysql: true,
        },
      },
      {
        name: 'production',
        status: 'active',
        operators: {
          mongodb: true,
          postgresql: true,
          mysql: true,
        },
      },
      {
        name: 'staging',
        status: 'active',
        operators: {
          mongodb: false,
          postgresql: true,
          mysql: true,
        },
      },
    ];
  }
}

export const systemService = new SystemService();
